import { useRef, useState } from 'react';
import { View } from 'react-native';
import { useRouter } from 'expo-router';

import { Dialog, DialogButton, Text } from '@/components/ui';
import {
  createSmartImportFromFile,
  createSmartImportFromPaste,
} from '@/lib/finance/smart-import';

type SmartImportDialogProps = {
  open: boolean;
  onClose: () => void;
};

export function SmartImportDialog({ open, onClose }: SmartImportDialogProps) {
  const router = useRouter();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [pasteText, setPasteText] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setPasteText('');
    setSelectedFile(null);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function closeDialog() {
    reset();
    onClose();
  }

  async function onSubmit() {
    setSubmitting(true);
    setError(null);
    try {
      const batch = selectedFile
        ? await createSmartImportFromFile(selectedFile)
        : await createSmartImportFromPaste(pasteText);
      closeDialog();
      router.push(`/finance/transactions/smart-import/${batch.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmit = selectedFile != null || pasteText.trim().length > 0;

  return (
    <Dialog
      open={open}
      title="Import transactions"
      onClose={closeDialog}
      footer={
        <>
          <DialogButton label="Cancel" onPress={closeDialog} disabled={submitting} />
          <DialogButton
            label={submitting ? 'Uploading…' : 'Continue'}
            variant="primary"
            onPress={() => void onSubmit()}
            disabled={!canSubmit || submitting}
          />
        </>
      }
    >
      <Text variant="body-sm" className="mb-3 text-muted">
        Upload an Excel workbook, CSV, text-based PDF, or paste statement text. An AI parser
        will extract transactions for review before import.
      </Text>

      <View className="mb-4">
        <Text variant="label" className="mb-1">
          Upload file
        </Text>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.tsv,.txt,.pdf,.xlsx,text/csv,text/plain,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          disabled={pasteText.trim().length > 0 || submitting}
          className="block w-full text-sm"
          onChange={(event) => {
            const file = event.target.files?.[0] ?? null;
            setSelectedFile(file);
            if (file) setPasteText('');
          }}
        />
        {selectedFile ? (
          <Text variant="caption" className="mt-1 text-muted">
            {selectedFile.name} ({Math.round(selectedFile.size / 1024)} KB)
          </Text>
        ) : null}
      </View>

      <Text variant="caption" className="mb-2 text-center text-muted">
        or
      </Text>

      <View>
        <Text variant="label" className="mb-1">
          Paste text
        </Text>
        <textarea
          className="cs-input min-h-32 w-full resize-y font-mono text-sm"
          placeholder="Paste CSV rows or copied statement text…"
          value={pasteText}
          disabled={selectedFile != null || submitting}
          onChange={(event) => {
            setPasteText(event.target.value);
            if (event.target.value.trim()) {
              setSelectedFile(null);
              if (fileInputRef.current) fileInputRef.current.value = '';
            }
          }}
        />
      </View>

      {error ? (
        <Text variant="body-sm" className="mt-3 text-danger">
          {error}
        </Text>
      ) : null}
    </Dialog>
  );
}
